/**
 * PromptsList — /crm/settings/prompts. The CRM's editable AI prompts (admin only).
 *
 * Each prompt's built-in default lives in code (crmPromptCatalog.js); an admin
 * can override + version any of them in the editor. A row shows whether the
 * prompt is still on the built-in default or has been customized (v{n}).
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Sparkles } from 'lucide-react';
import api from '../../../services/api';
import { relTime, useIsCRMAdmin } from '../../lib';
import { Alert } from '../../components/ui';
import PageHeader from '../../components/PageHeader';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

interface PromptRow {
  key: string;
  name: string;
  description: string;
  kind: 'system' | 'user';
  output_format: string;
  customized: boolean;
  version: number;
  status: string;
  updated_at: string | null;
}

function kindPill(kind: string): string {
  return kind === 'system'
    ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
    : 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300';
}

export default function PromptsList() {
  const navigate = useNavigate();
  const isAdmin = useIsCRMAdmin();
  const [prompts, setPrompts] = useState<PromptRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/crm/prompts');
      setPrompts(res.data?.prompts || []);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
        <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            AI prompt editing is available to CRM admins only.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
      <PageHeader
        icon={Sparkles}
        title="AI Prompts"
        subtitle="The instructions the AI follows when it turns study documents into summaries, recruiter scripts, stipend info, and prescreening questions. Edit one to change every future generation — each save is versioned so you can review or restore past wording."
      />

      {error && (
        <Alert tone="error" icon={false} className="mt-4">{error}</Alert>
      )}

      <div className="mt-5">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {prompts.map((p) => (
                <button
                  key={p.key}
                  onClick={() => navigate(`/crm/settings/prompts/${p.key}`)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left transition-colors duration-100 hover:bg-gray-50 dark:hover:bg-gray-700/50 sm:px-5"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="min-w-0 truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{p.name}</span>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${kindPill(p.kind)}`}>
                        {p.kind === 'system' ? 'system' : 'prompt'}
                      </span>
                    </div>
                    <p className="mt-0.5 line-clamp-2 text-xs text-gray-400">{p.description}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    {p.customized ? (
                      <>
                        <p className="text-sm font-semibold text-primary-700 dark:text-primary-400">v{p.version}</p>
                        <p className="text-[11px] text-gray-400">edited {relTime(p.updated_at)}</p>
                      </>
                    ) : (
                      <p className="text-[11px] text-gray-400">Built-in default</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
